import { createContext, useContext, useState } from "react";
import { classNames, FormFieldProps, useFormField } from "zeta-dom-react";
import { assign } from "src/util/common";

const CheckboxListContextProvider = createContext<CheckboxListContext | null>(null);

export interface CheckboxListProps extends FormFieldProps<string[]> {
    orientation?: 'horizontal' | 'vertical';
}

export class CheckboxListContext {
    readonly name?: string;
    readonly disabled?: boolean;

    constructor(
        private readonly setValueCallback: React.Dispatch<React.SetStateAction<string[]>>
    ) { }

    setValue(value: string, checked: boolean) {
        this.setValueCallback(v => {
            let set = new Set(v);
            if (checked) {
                set.add(value);
            } else {
                set.delete(value);
            }
            return [...set.values()];
        });
    }
}

export function useCheckboxListContext() {
    return useContext(CheckboxListContextProvider);
}

export function CheckboxList(props: React.PropsWithChildren<CheckboxListProps>) {
    const { error, setValue, elementRef } = useFormField({ isEmpty: v => !v?.length, ...props }, []);
    const [state] = useState(() => new CheckboxListContext(setValue));
    assign(state, {
        name: props.name,
        disabled: props.disabled
    });
    return (
        <fieldset ref={elementRef} className={classNames('eac-field eac-checkbox-list', { vertical: props.orientation === 'vertical', disabled: props.disabled, 'has-error': !!error })}>
            <CheckboxListContextProvider.Provider value={state}>{props.children}</CheckboxListContextProvider.Provider>
            {error && props.showErrorMessage !== false &&
                <div className="eac-field-error">{error}</div>}
        </fieldset>
    );
}
